/**
 * The application root (task 9.1).
 *
 * Owns the router, the one polite live region, the How to Play overlay, and the
 * loading of saved data. It owns no mission knowledge: every route below reads
 * the catalog and the packages, and none of them names a mission.
 */
import { Suspense, lazy, useEffect, type JSX } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { HelpOverlay } from './components/HelpOverlay.js';
import { LiveRegion } from './components/LiveRegion.js';
import { UpdatePrompt } from './components/UpdatePrompt.js';
import { ContentProvider } from './lib/content-context.js';
import type { ContentSource } from './lib/content/delivery.js';
import { applySettings } from './lib/settings.js';
import type { SaveStore } from './lib/save/store.js';
import { activeProfileOf } from './lib/save/types.js';
import { CareerScreen } from './screens/CareerScreen.js';
import { HowToPlayScreen } from './screens/HowToPlayScreen.js';
import { MissionSelectScreen } from './screens/MissionSelectScreen.js';
import { SettingsScreen } from './screens/SettingsScreen.js';
import { TitleScreen } from './screens/TitleScreen.js';
import { useViewStore } from './store/view-store.js';
import type { PlayRoute as PlayRouteComponent } from './screens/play/PlayRoute.js';

/**
 * The launch/play split (task 19.4).
 *
 * The launch surfaces — title, mission select, career, settings, How to Play —
 * are what a cold visitor pays for, and none of them needs the rules engine,
 * the chat workspace, the composer, or the drawer library those pull in. The
 * play surface is one import boundary wide, so deferring `PlayRoute` moves all
 * of it behind a second request that is made when a mission is actually
 * started. `scripts/assert-budget.ts` fails the build if the two chunks stop
 * being separate, or if either outgrows its budget.
 *
 * The chunk is *prefetched* once the shell is idle rather than left until the
 * route renders, so by the time a player has picked a mission it is already
 * there and the Suspense fallback below is unreachable in practice. It is still
 * correct if the fetch is slow, and it still costs a cold visitor nothing
 * before first paint.
 */
const playSurface = (): Promise<{ default: typeof PlayRouteComponent }> =>
  import('./screens/play/PlayRoute.js').then((module) => ({ default: module.PlayRoute }));

const PlayRoute = lazy(playSurface);

/**
 * "Match my device" has to keep matching it: a player who changes their system
 * theme or turns on Reduce Motion while the game is open should see it happen.
 */
function useSystemPreferences(): void {
  const doc = useViewStore((state) => state.doc);

  useEffect(() => {
    if (!doc || typeof globalThis.matchMedia !== 'function') return;
    const queries = [
      globalThis.matchMedia('(prefers-color-scheme: dark)'),
      globalThis.matchMedia('(prefers-reduced-motion: reduce)'),
    ];
    const reapply = (): void => {
      applySettings(document.documentElement, activeProfileOf(doc).settings);
    };
    for (const query of queries) query.addEventListener('change', reapply);
    return () => {
      for (const query of queries) query.removeEventListener('change', reapply);
    };
  }, [doc]);
}

/**
 * Warms the play chunk once the shell has stopped being busy.
 *
 * `requestIdleCallback` where it exists — Safari still does not have it — and a
 * short timeout everywhere else. Failure is ignored on purpose: this is an
 * optimisation, and a prefetch that fails offline must not surface as an error
 * when the route itself would load from the service worker cache anyway.
 */
function usePlaySurfacePrefetch(): void {
  useEffect(() => {
    const warm = (): void => {
      void playSurface().catch(() => undefined);
    };
    const idle = (globalThis as { requestIdleCallback?: (cb: () => void) => number })
      .requestIdleCallback;
    if (idle) {
      idle(warm);
      return;
    }
    const timer = globalThis.setTimeout(warm, 1_000);
    return () => {
      globalThis.clearTimeout(timer);
    };
  }, []);
}

function SkipLink(): JSX.Element {
  return (
    <a
      href="#main"
      data-testid="skip-to-content"
      // `touch-target` matters only in the focused state, which is the only
      // state this link has a box in — but that is the state a player uses it
      // in, and 44x44 applies there like anywhere else (task 19.1).
      className="sr-only touch-target items-center rounded-lg bg-accent px-4 py-2 text-accent-ink focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:inline-flex"
    >
      Skip to content
    </a>
  );
}

function NotFound(): JSX.Element {
  return <Navigate to="/" replace />;
}

export function AppRoutes(): JSX.Element {
  useSystemPreferences();
  usePlaySurfacePrefetch();

  return (
    <>
      <SkipLink />
      <Routes>
        <Route path="/" element={<TitleScreen />} />
        <Route path="/missions" element={<MissionSelectScreen />} />
        <Route
          path="/play/:missionId"
          element={
            <Suspense
              fallback={
                <p role="status" data-testid="loading" className="p-6 text-sm text-ink-muted">
                  Loading the mission…
                </p>
              }
            >
              <PlayRoute />
            </Suspense>
          }
        />
        <Route path="/career" element={<CareerScreen />} />
        <Route path="/settings" element={<SettingsScreen />} />
        <Route path="/how-to-play" element={<HowToPlayScreen />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <HelpOverlay />
      <UpdatePrompt />
      <LiveRegion />
    </>
  );
}

export interface AppProps {
  /** Injected by tests; the real one is built on first render otherwise. */
  readonly contentSource?: ContentSource | undefined;
  readonly saveStore?: SaveStore | undefined;
}

export function App({ contentSource, saveStore }: AppProps = {}): JSX.Element {
  const ready = useViewStore((state) => state.ready);
  const init = useViewStore((state) => state.init);

  useEffect(() => {
    // Loading is asynchronous now that the document lives in IndexedDB. Nothing
    // renders until it resolves, so there is nothing here to await.
    void init(saveStore);
  }, [init, saveStore]);

  // Nothing renders against a half-loaded save: the title screen's Continue and
  // every screen's theme depend on it, and a frame painted without it flickers.
  if (!ready) {
    return (
      <p role="status" className="p-6 text-sm text-ink-muted">
        Loading…
      </p>
    );
  }

  return (
    <ContentProvider source={contentSource}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </ContentProvider>
  );
}
